import axios from 'axios';
import type { Account, SiteAccount, OwnedAccount } from '@hotjar/account';
import { experimentViewed } from '@hotjar/tracking';
import type { TrackingSite } from '@hotjar/webapp/tracking/utils';
import { optimizely } from './optimizely';
import { BaseTarget as ExperimentTarget } from './targets/BaseTarget';
import { AccountTarget } from './targets/AccountTarget';
import { getVariantFromUrl } from './overrides';
import { VARIANTS } from './constants';

const MISSING_EXPERIMENT_ID = 'No experiment ID provided.';
const MISSING_EXPERIMENT_TARGET = 'No valid experiment target provided.';

export interface ExperimentData {
  variant: VARIANTS;
  inExperiment: boolean;
  target: ExperimentTarget;
}

interface ExperimentVariantResource {
  success: boolean;
  variant: VARIANTS;
}

export class ExperimentService {
  // Cached ExperimentData indexed by experiment ID
  private cache: Record<string, Promise<ExperimentData>> = {};

  private readonly defaults: Pick<ExperimentData, 'variant' | 'inExperiment'> = <const>{
    variant: VARIANTS.control,
    inExperiment: false,
  };

  clearCache(): void {
    this.cache = {};
  }

  async activateOnFrontend(experiment: string, target: ExperimentTarget): Promise<ExperimentData> {
    const { attributes } = target;
    const fallbackResult = { ...this.defaults, target };

    try {
      const variant: VARIANTS | null = await optimizely.activate(experiment, target.id, attributes);
      return variant ? { variant, target, inExperiment: true } : fallbackResult;
    } catch {
      return fallbackResult;
    }
  }

  async startAccountExperiment(
    experiment: string,
    account: Account | SiteAccount | OwnedAccount,
    site?: TrackingSite,
  ): Promise<ExperimentData> {
    const target = new AccountTarget(account);

    if (this.cache[experiment] === undefined) {
      this.cache[experiment] = this.activateOnFrontend(experiment, target);
      this.cache[experiment].then((data) => {
        if (data.inExperiment) {
          experimentViewed({
            experimentId: experiment,
            experimentTargetId: String(account.id),
            variationId: data.variant,
            site,
          });
        }
      });
    }

    return this.cache[experiment];
  }

  async activateOnBackend(
    experiment: string,
    target: ExperimentTarget,
    trackingData: Record<string, unknown>,
  ): Promise<ExperimentData> {
    const attributes = { ...target.attributes, ...trackingData };
    const fallbackResult = { ...this.defaults, target };
    const variantInUrl = getVariantFromUrl(experiment);

    if (variantInUrl) {
      return { inExperiment: true, variant: variantInUrl, target };
    }

    const { data } = await axios.get<ExperimentVariantResource>(
      `/api/v1/experiments/${experiment}/variant`,
      {
        params: {
          attributes,
          target_id: target.id,
          skip_impression: false,
        },
      },
    );

    if (data.success && data.variant) {
      return { variant: data.variant, inExperiment: true, target };
    }

    return fallbackResult;
  }

  getVariant(experiment: string, target: ExperimentTarget): Promise<ExperimentData> {
    if (!experiment) {
      return Promise.reject(MISSING_EXPERIMENT_ID);
    }

    if (!(target instanceof ExperimentTarget) || !target.validate()) {
      return Promise.reject(MISSING_EXPERIMENT_TARGET);
    }

    if (this.cache[experiment] === undefined) {
      this.cache[experiment] = this.activateOnFrontend(experiment, target);
    }

    return this.cache[experiment];
  }

  getVariantFromBackend(
    experiment: string,
    target: ExperimentTarget,
    trackingData = {},
  ): Promise<ExperimentData> {
    if (!experiment) {
      return Promise.reject(MISSING_EXPERIMENT_ID);
    }

    if (!(target instanceof ExperimentTarget) || !target.validate()) {
      return Promise.reject(MISSING_EXPERIMENT_TARGET);
    }

    if (this.cache[experiment] === undefined) {
      this.cache[experiment] = this.activateOnBackend(experiment, target, trackingData);
    }

    return this.cache[experiment];
  }
}
